import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useLogin } from "@/features/auth/api/useAuth";
import { loginSchema, type LoginValues } from "@/features/auth/lib/schemas";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Field } from "@/shared/ui/Field";

export function LoginPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const login = useLogin();
  const errorMessage = useApiErrorMessage();
  const { redirect } = useSearch({ from: "/public/login" });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  // `mutate` et non `mutateAsync` : ce dernier rejette quand l'API refuse, et
  // la rejection n'a personne pour la rattraper — la console du navigateur se
  // remplit d'erreurs non gérées à chaque mot de passe erroné. L'état d'échec
  // est déjà porté par la mutation.
  const onSubmit = handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: () => {
        void navigate({ to: redirect ?? "/account" });
      },
    });
  });

  return (
    <Card className="mx-auto max-w-md">
      <h1 className="mb-6 text-2xl font-bold tracking-tight">{t("login.heading")}</h1>

      {login.isError && (
        <Alert tone="error" className="mb-4">
          {errorMessage(login.error)}
        </Alert>
      )}

      <form
        onSubmit={(event) => {
          // `handleSubmit` rend toujours une fonction asynchrone, même sur un
          // gestionnaire synchrone : `void` dit explicitement qu'on n'attend
          // pas la promesse, ce que React ne saurait pas faire de toute façon.
          void onSubmit(event);
        }}
        className="space-y-4"
        noValidate
      >
        <Field
          label={t("fields.email")}
          type="email"
          autoComplete="email"
          error={errors.email && t("validation.email")}
          {...register("email")}
        />
        <Field
          label={t("fields.password")}
          type="password"
          autoComplete="current-password"
          error={errors.password && t("validation.required")}
          {...register("password")}
        />
        <Button type="submit" className="w-full" disabled={login.isPending}>
          {login.isPending ? t("login.submitting") : t("login.submit")}
        </Button>
      </form>

      <div className="mt-6 flex flex-col gap-2 text-sm">
        <Link to="/forgot-password" className="text-brand-600 hover:underline">
          {t("login.forgotPassword")}
        </Link>
        <p className="text-slate-600 dark:text-slate-400">
          {t("login.noAccount")}{" "}
          <Link to="/register" className="text-brand-600 hover:underline">
            {t("login.registerLink")}
          </Link>
        </p>
      </div>
    </Card>
  );
}
